"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, Sparkles, MapPin, Star, Code as Code2, ChartBar as BarChart3, CircleCheck as CheckCircle2, ArrowRight, Calendar } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const processSteps = [{
  number: "01",
  title: "Technical & Content SEO Audit",
  phase: "Audit Phase",
  icon: Search,
  description: "I personally analyze your site to identify technical hurdles, crawl issues, content gaps, and high-impact ranking opportunities.",
  highlights: ["Full site crawl & indexing check", "Competitor search gap analysis", "Prioritized technical action plan"]
}, {
  number: "02",
  title: "On-Page Optimization & Strategy",
  phase: "Optimization Phase",
  icon: Sparkles,
  description: "I align your site's structure and existing content with authentic search intent to target high-value national or local keywords.",
  highlights: ["Search-intent keyword mapping", "Metadata & internal link structure", "Strategic landing page optimization"]
}, {
  number: "03",
  title: "Local & Entity Citations",
  phase: "Citations Phase",
  icon: MapPin,
  description: "To establish geographical authority and brand trust, I build accurate citations and ensure consistent NAP data across core platforms.",
  highlights: ["Google Business Profile alignment", "Authoritative directory placements", "Consistent entity verification"]
}, {
  number: "04",
  title: "Review Strategy & Social Proof",
  phase: "Reviews Phase",
  icon: Star,
  description: "I implement clean systems to turn satisfied clients into public reviews that boost both search trust and user conversion rates.",
  highlights: ["Authentic review acquisition strategy", "Reputation signal optimization", "Trust bar conversion enhancement"]
}, {
  number: "05",
  title: "Structured Data & AI Schema",
  phase: "Schema Phase",
  icon: Code2,
  description: "I implement comprehensive JSON-LD schema so Google and modern AI search engines immediately comprehend your key services.",
  highlights: ["Custom organization & service schema", "AI search visibility markup", "Entity relationship definitions"]
}, {
  number: "06",
  title: "Transparent Reporting & Iteration",
  phase: "Reporting Phase",
  icon: BarChart3,
  description: "You receive clear, direct updates detailing keyword rankings, organic traffic trends, and genuine lead conversions.",
  highlights: ["No-fluff conversion metric reports", "Direct 1-on-1 strategy call with me", "Continuous iterative roadmap updates"]
}];
export default function Methodology() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="methodologyprocess" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-16 md:mb-20">
          <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground border border-border">
            Hands-On Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-default">
            My Proven 6-Step SEO Methodology
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            When you work with me, there are no black-box secrets or generic agency templates. 
            I execute a tailored, step-by-step framework to build sustainable organic growth for your business.
          </p>
        </div>

        {/* Process Steps Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative">
          {processSteps.map((step, i) => {
          const IconComponent = step.icon;
          return <motion.div key={step.number} data-index={i} variants={cardVariants} className="relative h-full flex flex-col">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 relative flex flex-col justify-between overflow-hidden">
                  {/* Phase accent line */}
                  <div className="h-1.5 w-full bg-secondary" />

                  <CardHeader className="pt-6 pb-4">
                    <div className="flex items-center justify-between mb-3">
                      {/* Step Number */}
                      <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary text-primary-foreground font-bold text-base shadow-sm">
                        {step.number}
                      </span>
                      
                      {/* Phase Badge */}
                      <span className="text-xs font-medium px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground">
                        {step.phase}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 mt-2">
                      <div className="p-2 rounded-md bg-muted text-foreground">
                        <IconComponent className="size-5 text-primary" />
                      </div>
                      <CardTitle className="text-xl font-semibold font-default text-card-foreground">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 flex-1 flex flex-col justify-between pt-0 pb-6">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {step.description}
                    </p>

                    <div className="pt-4 border-t border-border space-y-2 mt-auto">
                      <p className="text-xs font-semibold text-foreground tracking-wide uppercase">
                        Key Deliverables
                      </p>
                      <ul className="space-y-1.5">
                        {step.highlights.map((item, idx) => <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                            <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Connecting lines for desktop */}
                {i < processSteps.length - 1 && <div className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                    <div className="w-8 h-0.5 bg-secondary" />
                  </div>}
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom CTA Card */}
        <div className="mt-16 md:mt-20 rounded-2xl bg-muted/60 border border-border p-8 md:p-10 text-center max-w-4xl mx-auto space-y-6">
          <div className="space-y-2">
            <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground">
              Ready for a Transparent, Results-Driven SEO Strategy?
            </h3>
            <p className="text-muted-foreground text-sm md:text-base max-w-2xl mx-auto">
              You work directly with me—Yassine Essaadi. No account managers, no junior staff, just clear expert guidance aimed at driving genuine business growth.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ContactPage">
                <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                Book a Free SEO Consultation
              </Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}